/**
 * BORROW: the credit product marketplace (FR-U-CRD-009).
 *
 * Every product on this screen, and every number on every card, comes from the backend's product
 * configuration. Nothing is hardcoded here, and that is not a style preference: FR-A-CRD-001 makes
 * credit policy something a risk officer changes under maker-checker without a deploy, and a rate
 * that lives in a React component cannot be changed, withdrawn, or produced in evidence.
 *
 * The screen also answers the question that decides whether the borrower can proceed at all: do they
 * hold an eligible tokenized asset? Borrowing here is SECURED, always, so a participant with nothing
 * to pledge is sent to tokenization rather than into a form that will refuse them at the last step.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type CreditProduct, type EligibleCollateral } from '@/lib/api';
import { PageHeader, SectionTitle } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { formatWireAmount } from '@/lib/money';

const FREQUENCY: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  bullet: 'One payment at maturity',
  seasonal: 'Seasonal',
};

const CLASS_LABEL: Record<string, string> = {
  real_estate: 'Property',
  land: 'Land',
  vehicle: 'Vehicles',
  inventory: 'Inventory',
  receivable: 'Receivables',
};

export default function CreditBorrow() {
  const [products, setProducts] = useState<CreditProduct[] | null>(null);
  const [collateral, setCollateral] = useState<EligibleCollateral[]>([]);
  const [hasEligible, setHasEligible] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, c] = await Promise.all([
        api.creditMarket.products(),
        api.creditMarket.eligibleCollateral(),
      ]);
      setProducts(p.products);
      setCollateral(c.collateral);
      setHasEligible(c.has_any);
    } catch (e) {
      setError(
        e instanceof LipaApiError ? e.detail.message : 'We could not load the credit products.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!products) return <LoadingCard lines={5} />;

  /** Which classes the borrower can actually pledge, so a product they cannot use says so. */
  const usableClasses = new Set(collateral.filter((c) => c.eligible).map((c) => c.asset_class));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Borrow"
        subtitle="Secured financing against a tokenized asset you already own."
      />

      {/*
        THE GATE, AND THE WAY THROUGH IT.
        Section 6: no eligible tokenized asset means no secured credit request. The screen says so
        BEFORE the borrower fills anything in, and hands them the route rather than a refusal.
      */}
      {!hasEligible && (
        <Reveal>
          <section className="rounded-card border border-warning/40 bg-warning/5 p-4">
            <p className="text-sm font-medium text-ink">
              You do not currently have an eligible tokenized asset
            </p>
            <p className="mt-1.5 text-micro leading-relaxed text-ink-muted">
              Borrowing here is secured on a tokenized asset. To borrow against something you own,
              tokenize and list the asset first, then come back.
            </p>
            {collateral.length > 0 && (
              <ul className="mt-3 space-y-1.5 border-t border-warning/20 pt-3">
                {collateral.map((c) => (
                  <li key={c.token_id} className="text-micro text-ink-muted">
                    <span className="font-medium text-ink">{c.asset_name}</span>
                    {c.reasons.length > 0 && <> &mdash; {c.reasons[0]}</>}
                  </li>
                ))}
              </ul>
            )}
            <Link
              to="/tokenize/new"
              className="mt-3 inline-block rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
            >
              Tokenize an asset
            </Link>
          </section>
        </Reveal>
      )}

      <section>
        <SectionTitle>Financing products</SectionTitle>
        <ul className="space-y-3">
          {products.map((p) => {
            const usable =
              hasEligible && p.asset_classes.some((cls) => usableClasses.has(cls));
            return (
              <li key={p.id}>
                <Reveal>
                  <article className="rounded-card border border-border bg-surface p-4 shadow-card">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ink">{p.name}</p>
                        <p className="text-micro text-ink-subtle">{p.lender_name}</p>
                      </div>
                      <span className="tabular shrink-0 rounded-field bg-brand/10 px-2 py-1 text-micro font-medium text-brand">
                        {(p.rate_bps / 100).toFixed(2)}% p.a.
                      </span>
                    </div>

                    {p.description && (
                      <p className="mt-2 text-micro leading-relaxed text-ink-muted">
                        {p.description}
                      </p>
                    )}

                    <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3 sm:grid-cols-4">
                      <Fact label="Max LTV" value={`${p.max_ltv_bps / 100}%`} />
                      <Fact
                        label="Amount"
                        value={
                          p.min_amount_minor && p.max_amount_minor
                            ? `${formatWireAmount(p.min_amount_minor)} – ${formatWireAmount(p.max_amount_minor)}`
                            : 'On application'
                        }
                      />
                      <Fact
                        label="Tenor"
                        value={
                          p.tenor_options_months.length > 0
                            ? `${p.tenor_options_months[0]}–${p.tenor_options_months[p.tenor_options_months.length - 1]} months`
                            : `${Math.round(p.tenor_days / 30)} months`
                        }
                      />
                      <Fact
                        label="Repayment"
                        value={FREQUENCY[p.repayment_frequency] ?? p.repayment_frequency}
                      />
                    </dl>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.asset_classes.map((cls) => (
                        <span
                          key={cls}
                          className="rounded-field bg-surface-sunken px-2 py-0.5 text-nano text-ink-muted"
                        >
                          {CLASS_LABEL[cls] ?? cls}
                        </span>
                      ))}
                    </div>

                    {p.eligibility_notes && (
                      <p className="mt-2.5 text-nano leading-relaxed text-ink-subtle">
                        {p.eligibility_notes}
                      </p>
                    )}

                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
                      <span className="text-nano text-ink-subtle">
                        Grace period {p.grace_period_days} days · {p.required_documents.length}{' '}
                        documents required
                      </span>
                      {usable ? (
                        <Link
                          to={`/credit/apply?product=${p.id}`}
                          className="shrink-0 rounded-field bg-primary px-3 py-1.5 text-micro font-medium text-white transition hover:bg-primary-light"
                        >
                          Apply
                        </Link>
                      ) : (
                        <span
                          className="shrink-0 rounded-field bg-surface-sunken px-3 py-1.5 text-micro font-medium text-ink-subtle"
                          title="You do not hold an eligible tokenized asset of a class this product accepts."
                        >
                          No eligible asset
                        </span>
                      )}
                    </div>
                  </article>
                </Reveal>
              </li>
            );
          })}
        </ul>
      </section>

      <p className="rounded-card bg-surface-sunken p-3 text-center text-xs leading-relaxed text-ink-muted">
        Rates, limits and fees shown here are the configured terms of each product. The exact terms
        you are offered come back from the financiers who bid on your request, itemised in full before
        you accept anything.
      </p>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-nano text-ink-subtle">{label}</dt>
      <dd className="tabular truncate text-micro font-medium text-ink">{value}</dd>
    </div>
  );
}
